import { Box, Paper, Table, TableBody, TableContainer, TableHead } from "@mui/material";
import SupplyTableRow, { DND_ITEM_TYPES } from "./supplyTableRow";
import SupplyTableHeaderRow from "./supplyTableHeaderRow";
import SupplyTableRowDragPreview from "./SupplyTableRowDragPreview";
import SupplyTableGhostRow from "./SupplyTableGhostRow";
import { useEffect, useState, useCallback, useRef } from "react";
import { getOrigin } from "../../../../http/api/MasterAPI";
import { useDrop } from "react-dnd";
import { useDragDropManager } from "react-dnd";
import { SUPPLY_TABLE_ROW_COUNT_PER_PAGE } from "../hook/useSupplyListForm";

// ghost swap loop 防止用のスロットリング (ms)。
// react-dnd は mousemove ごとに hover を連発するため、間隔を空けないと
// 1 mousemove で複数 swap が発火 → current ghost 位置を見て newVis を再計算 →
// 逆向き swap … と振動する。
// この間隔は DOM 反映を待つ + 連発吸収の役目。短すぎると振動、長すぎるとカクつく。
const GHOST_SWAP_THROTTLE_MS = 500;

const formatSupplyProduct = (product, lastProduct) => {

  if (
    lastProduct
    && product.productName == lastProduct.productName
  ) {
    return {...product, productName: "〃"}
  }

  return product;

}

function SupplyTable({supplyForm, suppliers, enabledDisplayRemarks, keyHandler, onHideProduct, onShowProduct, onShowProductAt, onAddProduct, onAddProductAt, currentPage, onChangePage}) {
  const [origins, setOrigins] = useState([]);
  const [recentlyDroppedIndex, setRecentlyDroppedIndex] = useState(null);
  // ghostInsertAt: { visibleIndex, item } | null  ── sidebar からのドラッグ時に挿入位置プレビューを表示
  const [ghostInsertAt, setGhostInsertAt] = useState(null);
  // drop イベントで参照するため ref にも保持 (cursor が ghost row 上にあると row 側 drop が発火しないため)
  const ghostInsertAtRef = useRef(null);
  const wasDragging = useRef(false);
  const lastHoverIndex = useRef(null);
  // 直近 ghost swap した時刻 (performance.now())。
  // 次の swap は GHOST_SWAP_THROTTLE_MS 経過後まで block する。
  // (rapid hover 発火による oscillation 防止)
  const lastGhostSwapTimeRef = useRef(0);
  const dragDropManager = useDragDropManager();

  // Monitor drag state: when drag ends, highlight the destination row
  useEffect(() => {
    const monitor = dragDropManager.getMonitor();
    const unsubscribe = monitor.subscribeToStateChange(() => {
      const isDragging = monitor.isDragging();

      // Drag just started → clear previous "recently dropped" highlight so the
      // user can clearly distinguish the new destination from the old one
      if (!wasDragging.current && isDragging) {
        setRecentlyDroppedIndex(null);
        lastGhostSwapTimeRef.current = 0;
      }

      // Drag just ended → highlight at destination position + clear ghost
      if (wasDragging.current && !isDragging) {
        if (lastHoverIndex.current !== null) {
          setRecentlyDroppedIndex(lastHoverIndex.current);
          lastHoverIndex.current = null;
        }
        setGhostInsertAt(null);
        ghostInsertAtRef.current = null;
        lastGhostSwapTimeRef.current = 0;
      }

      wasDragging.current = isDragging;
    });
    return () => unsubscribe();
  }, [dragDropManager]);

  // ghost 挿入位置を直接更新するヘルパー (bottomDrop / 末尾挿入用)
  const setGhostInsertAtIndex = useCallback((insertAt, item) => {
    const current = ghostInsertAtRef.current;
    if (current && current.visibleIndex === insertAt) return;
    const next = { visibleIndex: insertAt, item };
    ghostInsertAtRef.current = next;
    setGhostInsertAt(next);
  }, []);

  // sidebar からドラッグ中、行 hover 時に ghost row 位置を更新 (swap 方式)
  // ghost が row の下にある (current > rowVisibleIndex) → ghost を row の手前へ
  // ghost が row の上にある or 同位置 (current <= rowVisibleIndex) → ghost を row の後ろへ
  // これにより「枠に触れた瞬間に入れ替わる」UX が実現される (PRODUCT_ROW reorder と同じ感覚)。
  //
  // throttle: 前回 swap から GHOST_SWAP_THROTTLE_MS 未満なら skip。
  // react-dnd は同じ位置でも hover を高頻度で連発するため、間隔を空けないと
  // 1 mousemove 内で swap が複数発火 → 振動する。
  const handleGhostHover = useCallback((rowVisibleIndex, item) => {
    const now = performance.now();
    if (now - lastGhostSwapTimeRef.current < GHOST_SWAP_THROTTLE_MS) return;
    const current = ghostInsertAtRef.current;
    const newVisibleIndex =
      !current || rowVisibleIndex < current.visibleIndex
        ? rowVisibleIndex
        : rowVisibleIndex + 1;
    if (current && current.visibleIndex === newVisibleIndex) return;
    lastGhostSwapTimeRef.current = now;
    setGhostInsertAtIndex(newVisibleIndex, item);
  }, [setGhostInsertAtIndex]);

  // Handle move item during drag - save the destination index
  // moveSupplyDetail uses array indices and re-indexes supplyProductIndex after each move,
  // so after the move the dropped item's new supplyProductIndex === hoverIndex
  const handleMoveItem = useCallback((dragIndex, hoverIndex) => {
    lastHoverIndex.current = hoverIndex;
    supplyForm.moveSupplyDetail(dragIndex, hoverIndex);
  }, [supplyForm.moveSupplyDetail]);

  // visibleIndex を full forms 配列の supplyProductIndex に変換する
  // (ghost の挿入位置は visibleForms ベースだが、addSupplyDetailAt は全体配列の index を期待する)
  const resolveInsertIndex = (visibleIndex) => {
    const visible = supplyForm.forms.filter((f) => !f.isDelete);
    if (visibleIndex >= visible.length) {
      // 末尾挿入は「最後の visible item の次」。
      // supplyForm.forms.length を返すと page 2 以降で current page の前に挿入されてしまうため不可。
      if (visible.length === 0) return supplyForm.forms.length;
      return visible[visible.length - 1].supplyProductIndex + 1;
    }
    return visible[visibleIndex].supplyProductIndex;
  };

  // ページ末尾への挿入で、ページが満杯だった場合に次ページへ移動 + 画面トップへスクロール。
  // (新規 item は次ページ先頭に表示されるため)
  const navigateNextIfPageWasFull = (visibleIndex) => {
    const isAtEndOfPage = visibleIndex >= visibleForms.length;
    if (!isAtEndOfPage) return;
    if (visibleForms.length < SUPPLY_TABLE_ROW_COUNT_PER_PAGE) return;
    if (!onChangePage || currentPage == null) return;
    onChangePage(currentPage + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Drop zone for receiving dragged hidden products from sidebar, or new products from AddProduct sidebar
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: [DND_ITEM_TYPES.HIDDEN_ROW, DND_ITEM_TYPES.ADD_PRODUCT_ROW],
    drop: (item, monitor) => {
      // ADD_PRODUCT_ROW: 子 drop target は ghost 位置の決定だけ担当し commit はここで一括処理。
      // (cursor が ghost row 上にあると行 drop が発火しないこと、row 側で commit すると
      //  hover 中に決定した ghost 位置と乖離するため)
      if (item.type === DND_ITEM_TYPES.ADD_PRODUCT_ROW) {
        const ghost = ghostInsertAtRef.current;
        if (ghost && onAddProductAt) {
          onAddProductAt(item, item.part, resolveInsertIndex(ghost.visibleIndex));
          navigateNextIfPageWasFull(ghost.visibleIndex);
          return;
        }
        if (onAddProduct) onAddProduct(item, item.part);
        return;
      }
      // HIDDEN_ROW: 行 drop が処理済みならスキップ。残りは ghost or fallback。
      if (monitor.didDrop()) return;
      const ghost = ghostInsertAtRef.current;
      if (ghost && onShowProductAt && item.product) {
        onShowProductAt(item.product, resolveInsertIndex(ghost.visibleIndex));
        navigateNextIfPageWasFull(ghost.visibleIndex);
        return;
      }
      if (onShowProduct && item.product) {
        onShowProduct(item.product);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [onShowProduct, onAddProduct, onShowProductAt, onAddProductAt, supplyForm.forms, currentPage, onChangePage]);

  async function GetOrigin() {
    let response = await getOrigin({});
    let listOrigin = response.masterVarInfo;
    // 産地の並び: 地物系(varValue3='1')は後回し、旅系('2')・未分類を先頭へ。
    // ※ varValue2 は「よみがな」を保持しているため、区分フラグには varValue3 を使用。
    listOrigin = [...listOrigin]
      .sort((a, b) => (a.varValue3 === "1" ? 1 : 0) - (b.varValue3 === "1" ? 1 : 0))
      .map((o) => ({
        originId: o.varCd,
        originName: o.varValue1,
      }));
    setOrigins(listOrigin);
  }
  useEffect(() => {
    GetOrigin();
  }, []);

  const visibleForms = supplyForm.forms.filter((f) => !f.isDelete);

  const supplyTableRows = visibleForms.map((form, visIdx, arr) => (
    <SupplyTableRow
      key={`supply_product_${form.supplyProductIndex}`}
      supplyForm={
        visIdx == 0 ? form : formatSupplyProduct(form, arr[visIdx - 1])
      }
      originalForm={form}
      index={form.supplyProductIndex}
      visibleIndex={visIdx}
      suppliers={suppliers}
      changeSupplyQuantityDetailsAt={supplyForm.changeSupplyQuantityDetailsAt}
      enabledDisplayRemarks={enabledDisplayRemarks}
      keyHandler={keyHandler(visIdx)}
      origins={origins}
      onOriginChange={supplyForm.changeOrigin}
      onHideProduct={onHideProduct}
      onMoveItem={handleMoveItem}
      onShowProductAt={onShowProductAt}
      onGhostHover={handleGhostHover}
      isRecentlyDropped={form.supplyProductIndex === recentlyDroppedIndex}
    />
  ));

  // ghost row を挿入位置に差し込む (sidebar からドラッグ中のプレビュー)
  if (ghostInsertAt) {
    const insertAt = Math.min(ghostInsertAt.visibleIndex, supplyTableRows.length);
    supplyTableRows.splice(
      insertAt,
      0,
      <SupplyTableGhostRow
        key="__ghost__"
        item={ghostInsertAt.item}
        suppliers={suppliers}
        enabledDisplayRemarks={enabledDisplayRemarks}
      />
    );
  }

  // Bottom drop zone for moving items to the very last position on the page
  const [, bottomDrop] = useDrop(() => ({
    accept: [DND_ITEM_TYPES.PRODUCT_ROW, DND_ITEM_TYPES.HIDDEN_ROW, DND_ITEM_TYPES.ADD_PRODUCT_ROW],
    hover: (draggedItem) => {
      // sidebar (ADD_PRODUCT_ROW) → ghost を末尾に表示 (swap logic を経由せず直接挿入)
      if (draggedItem.type === DND_ITEM_TYPES.ADD_PRODUCT_ROW) {
        setGhostInsertAtIndex(visibleForms.length, draggedItem);
        // bottomDrop は handleGhostHover を経由しないため throttle 時刻を reset。
        // 行に戻った時に最初の hover が必ず swap を発火するようにする。
        lastGhostSwapTimeRef.current = 0;
        return;
      }
      if (draggedItem.type !== DND_ITEM_TYPES.PRODUCT_ROW) return;
      if (visibleForms.length === 0) return;
      const lastForm = visibleForms[visibleForms.length - 1];
      const lastIndex = lastForm.supplyProductIndex;
      if (draggedItem.index === lastIndex) return;
      handleMoveItem(draggedItem.index, lastIndex);
      draggedItem.index = lastIndex;
    },
    drop: (item, monitor) => {
      // ADD_PRODUCT_ROW は親 drop が ghost 位置を見て commit するためここでは何もしない
      if (item.type === DND_ITEM_TYPES.ADD_PRODUCT_ROW) return;
      if (monitor.didDrop()) return;
      if (item.type !== DND_ITEM_TYPES.HIDDEN_ROW) return;

      // 現在ページが空 → 末尾追加 (fallback)
      if (visibleForms.length === 0) {
        if (onShowProduct && item.product) onShowProduct(item.product);
        return;
      }

      // 末尾挿入 (= 現ページ末尾の次)。満杯なら新規 item は次ページ先頭に乗る。
      const endVisibleIndex = visibleForms.length;
      const insertIndex = resolveInsertIndex(endVisibleIndex);
      if (onShowProductAt && item.product) {
        onShowProductAt(item.product, insertIndex);
      }
      navigateNextIfPageWasFull(endVisibleIndex);
    },
  }), [visibleForms, handleMoveItem, setGhostInsertAtIndex, onShowProduct, onShowProductAt, currentPage, onChangePage]);

  return (
    <Box
      ref={drop}
      sx={{
        border: canDrop ? "2px dashed" : "2px solid transparent",
        borderColor: isOver && canDrop ? "success.main" : canDrop ? "grey.300" : "transparent",
        borderRadius: 1,
        bgcolor: isOver && canDrop ? "success.50" : "transparent",
        transition: "all 0.2s ease",
      }}
    >
      {/* Custom drag preview (overlay following cursor) */}
      <SupplyTableRowDragPreview />

      <TableContainer component={Paper} sx={{overflowX:"auto"}}>
        <Table>
          <TableHead >
            <SupplyTableHeaderRow suppliers={suppliers} />
          </TableHead>
          <TableBody>
            {supplyTableRows}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Bottom drop zone - allows dropping items to the very last position */}
      <Box ref={bottomDrop} sx={{ height: 48 }} />
    </Box>
  );
}

export default SupplyTable;
